import React, { useEffect, useRef, useState } from "react";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import { Legal, LegalVariant } from "@att-bit/duc.components.legal";
import { Links, LinksVariant } from "@att-bit/duc.components.links";
import { Modal, ModalSize, ModalVariant } from "@att-bit/duc.components.modal";
import { AnalyticsEvent } from "@att-bit/ui-frameworks.components.analytics-event";
import { docComplete } from "@att-bit/duc.utils.types";

// Fixing missing file issue;

export type LegalWrapperProps = {
  /** Legal wrapper options to send variant, id, overrideColor and className */
  options?: LegalWrapperOptions;
  /** LegalWrapperData includes legalText, legalLinkText, legalLink, legalModalSize, linkColor, legalModalTitle, legalLongText, linkAnalyticsData, modalLoadAnalyticsData */
  data: LegalWrapperData;
};

export type LegalWrapperData = {
  legalText: string;
  legalLinkText?: string;
  legalLink?: string;
  legalModalSize?: string;
  linkColor?: string;
  legalModalTitle?: string;
  legalLongText?: string;
  linkAnalyticsData?: any;
  modalLoadAnalyticsData?: any;
  onModalClose?: () => void;
  legalLinkAccessibilityText?: string;
};

export type LegalWrapperOptions = {
  variant?: any;
  id?: string;
  overrideColor?: string;
  className?: string;
  longLegalBtn?: boolean;
  btnSize?: string;
  fontType?: string;
  fontSize?: LegalSizes;
  asStandalone?: boolean;
  dockedModalHeader?: boolean;
};

export type LegalResponse = {
  accessibility?: string;
  legalModalTitle?: string;
  legalLongPath?: string;
  lineBreak?: boolean;
  error?: any;
};

export enum LegalSizes {
  XXXL = "heading-xxxl",
  XXL = "heading-xl",
  LG = "type-lg",
  MD = "type-md",
  BASE = "type-base",
  SM = "type-sm",
  XS = "type-xs",
  XXS = "type-xxs",
}

export const LegalWrapper = ({ data, options }: LegalWrapperProps) => {
  const [showModal, setShowModal] = useState(false);
  const [modalHeading, setModalHeading] = useState(null);
  const [modalBody, setModalBody] = useState(null);
  const [basePageData, setBasePageData] = useState(null);

  const legalModalPageLoad = data.modalLoadAnalyticsData ? data.modalLoadAnalyticsData : 
  {
    'page.location.url': "/legal/modal",
    'page.pageInfo.friendlyPageName': `UF Legal Modal Pg`,
    'page.pageInfo.flowCode': "DSUNK",
  }

  const textRef = useRef(null);
  useEffect(() => {
    const linkEleArr = textRef?.current?.querySelectorAll("a");
    if (linkEleArr) {
      linkEleArr?.forEach((linkItem: any) => {
        linkItem.addEventListener("click", handleClick);
      });
    }
  }, []);

  const handleClick = (e: any) => {
    const lnkname = e.target?.innerText;
    const destUrl =
      e.target?.nodeName === "A"
        ? e.target?.href
        : e.target?.parentElement?.nodeName === "A"
        ? e.target?.parentElement?.href
        : "";
    const lnkPos = "Body";
    const lnkdata = {
      linkDestinationUrl: destUrl,
      linkName: lnkname,
      linkPosition: lnkPos,
      ...data.linkAnalyticsData,
    };
    const lnkClickObj = {
      eventCode: "Link_Click",
      eventAction: "linkClick",
      additionalData: lnkdata,
    };
    //console.log("@@@@@@@@@@: ", lnkClickObj);
    docComplete(() => {
      AnalyticsEvent(lnkClickObj);
    });
  };

  const closeModal = () => {
    setShowModal(false);
    if (data.onModalClose) {
      data.onModalClose();
    } else {
      const closeData = {
        linkName: "Close Icon",
        linkPosition: "legal modal",
      };
      const lnkClickObj = {
        eventCode: "Link_Click",
        eventAction: "linkClick",
        additionalData: closeData,
      };
      docComplete(() => {
        AnalyticsEvent(lnkClickObj);
      });
      if (basePageData?.eventAction) {
        const pageData = {
          datatransform: false,
          'page.category.applicationName': basePageData["page.category.applicationName"],
          'page.category.pageFunction': basePageData["page.category.pageFunction"],
          'page.category.siteSection': basePageData["page.category.siteSection"],
          'page.location.url': basePageData["page.location.url"],
          'page.pageInfo.friendlyPageName': basePageData["page.pageInfo.friendlyPageName"],
          'page.pageInfo.lineOfBusiness': basePageData["page.pageInfo.lineOfBusiness"],
          'page.pageInfo.pageTitle': basePageData["page.pageInfo.pageTitle"],
          'page.pageInfo.flowCode': basePageData["page.pageInfo.flowCod"],
        };
        docComplete(() => {
          AnalyticsEvent({
            "eventCode": "Page_Load",
            "eventAction": "pageLoad",
            "additionalData": pageData
          });
        });
      }
    }
  };

  const addListDiv = (modContent) => {
    const addWrapperDiv = `<div class="list line-h-normal"><ul`;
    return (
      modContent &&
      modContent
        .replaceAll("<ul", addWrapperDiv)
        .replaceAll("</ul>", "</ul></div>")
    );
  };

  const getCollapsibleContent = (legalText: string) => {
    const detailStart = /#!/gm;
    const detailEnd = /!#/gm;
    const summaryStart = /#\*/gm;
    const summaryEnd = /\*#/gm;

    legalText = legalText.replace(detailStart, "<details>");
    legalText = legalText.replace(detailEnd, "</details>");
    legalText = legalText.replace(summaryStart, "<summary>");
    legalText = legalText.replace(summaryEnd, "</summary>");

    return legalText;
  }

  const getModalData = (e) => {
    e.preventDefault();
    setBasePageData(window["AnalyticsNotificationFramework"]?.getLandingPageData());
    if (data.legalLongText) {
      const modalHeading = (
        <div
          dangerouslySetInnerHTML={{
            __html: data.legalModalTitle || "",
          }}
        />
      );
      const tmpBodyStr = (data.legalLongText && addListDiv(data.legalLongText)) || "";
      const modalBody = (
        <div className="collapsibleSection"
          dangerouslySetInnerHTML={{
            __html: tmpBodyStr.length > 0 ? getCollapsibleContent(tmpBodyStr) : ''
          }}
        />
      );
      setModalHeading(modalHeading);
      setModalBody(modalBody);
      setShowModal(true);
    } else {
      const legalModalPath = e.currentTarget && e.currentTarget.href;
      fetch(legalModalPath)
        .then((response: Response): any => {
          return parseJSON(response.clone()); // can't this be just 'json()' ?
        })
        .then((data: any) => {
          const legalmodalContent: LegalResponse = getStaticLegal(
            data,
            undefined,
            legalModalPath
          );
          if (legalmodalContent != null) {
            const modalHeading = (
              <div
                dangerouslySetInnerHTML={{
                  __html: legalmodalContent.legalModalTitle || "",
                }}
              />
            );
            const tmpBodyStr = (legalmodalContent.legalLongPath && addListDiv(legalmodalContent.legalLongPath)) || "";
            const modalBody = (
              <div className="collapsibleSection"
                dangerouslySetInnerHTML={{
                  __html: tmpBodyStr.length > 0 ? getCollapsibleContent(tmpBodyStr) : ''
                }}
              />
            );
            setModalHeading(modalHeading);
            setModalBody(modalBody);
            setShowModal(true);
          }
        })
        .catch((error) => {});
    }
  };

  const getStaticLegal = (
    values: LegalResponse,
    callback?: (response: LegalResponse) => void,
    legalModalPath?: string
  ): LegalResponse => {
    const processedResponse: LegalResponse = processAjaxResponse(
      values,
      legalModalPath
    );
    if (callback) {
      callback(processedResponse);
    }
    return processedResponse;
  };

  const processAjaxResponse = (
    response: any,
    legalModalPath?: string
  ): LegalResponse => {
    const legalContent: LegalResponse = parseJsonResponse(
      response,
      legalModalPath
    );
    legalContent.legalLongPath = getLongLegalContent(
      legalContent.legalLongPath as string,
      legalContent.lineBreak
    );
    legalContent.legalLongPath = parseContent(
      legalContent.legalLongPath as string,
      legalContent.lineBreak
    );
    legalContent.legalModalTitle = parseContent(
      legalContent.legalModalTitle as string,
      legalContent.lineBreak
    );
    return legalContent;
  };

  const getLongLegalContent = (content: any, lineBreak?: boolean): string => {
    let newLegalContent: any = ``;
    if (content) {
      for (const key in content) {
        if (content[key]) {
          if (content[key].heading) {
            newLegalContent = newLegalContent + content[key].heading;
          }
          if (content[key].legalText) {
            //Dont add <p> if it already has it
            if (content[key].legalText.startsWith(`<p>`)) {
              newLegalContent = newLegalContent + content[key].legalText;
            } else {
              newLegalContent = newLegalContent + `<p>`;
              newLegalContent = newLegalContent + content[key].legalText;
              newLegalContent = newLegalContent + `</p>`;
            }
          }
        }
      }
      return newLegalContent;
    }
  };

  const parseContent = (content: string, lineBreak?: boolean): string => {
    if (content) {
      if (content !== `&nbsp;` && content !== ` `) {
        const tag: string = lineBreak ? `div` : `span`;
        const space: string = lineBreak ? `` : ` `;
        return `<${tag}>${content}${space}</${tag}>`;
        // return this.htmlToReact(`<${tag}>${content}${space}</${tag}>`);
      }
      return `%{NULL}`;
    }
    return undefined;
  };

  const parseJsonResponse = (
    response: any,
    legalModalPath?: string
  ): LegalResponse => {
    const page = legalModalPath
      .substring(legalModalPath.lastIndexOf("/") + 1)
      .replace(".cmsfeed.js", "");
    let tableFeed: any = response["idp-cms-feed"][page];
    tableFeed = fixEmpty(masterParser(tableFeed));
    const returnResponse: LegalResponse = {
      legalLongPath: tableFeed.legalLongPath,
      legalModalTitle: tableFeed.legalModalTitle,
    };
    return returnResponse;
  };
  const fixEmpty = (data: any) => {
    if (data) {
      return JSON.parse(
        JSON.stringify(data).replace(
          /(["'`]\<\/?empty[\s/]*\/?\>["'`])|(\{\})/g,
          `""`
        )
      );
    }
    return;
  };

  const masterParser = (nodeToCheck: any) => {
    const parse = (node: any): any => {
      if (node && typeof node === "object") {
        const master: any = node.master;
        if (master) {
          return { ...parse(master) };
        } else if (Array.isArray(node)) {
          return map(node, (val: any) => parse(val));
        }
        return mapValues(node, (val: any) => parse(val));
      }
      return node;
    };
    return parse(nodeToCheck);
  };

  const parseJSON = <T extends object = object>(res: Response): T => {
    return res.json() as T;
  };

  const renderLegal = () => {
    return (
      <div id={options?.id + "_Legal"}>
        <Legal
          className={`${
            options?.className ? options.className : "mar-b-sm-all"
          } ${options?.overrideColor ? options.overrideColor : ""} 
        ${options?.fontType ? options.fontType : ""}`}
          {...{
            variant:
              options?.variant &&
              options?.variant?.toLowerCase()?.includes("on_dark")
                ? LegalVariant.ON_DARK
                : options?.variant && options?.variant,
          }}
          addImplicitFontClass={!options.longLegalBtn}
        >
          {data.legalText && (
            <span ref={textRef}
              className={`${options.longLegalBtn ? "type-legal" : ""} ${
                options?.fontSize ? options.fontSize : ""
              }`}
              dangerouslySetInnerHTML={{ __html: data.legalText }}
            ></span>
          )}
          {data.legalLinkText &&
          data.legalLinkText.length > 0 &&
          !options.longLegalBtn ? (
            <>
              {" "}
              <Links
                {...(data.legalLink ? { href: data.legalLink } : "")}
                onClick={getModalData}
                className={`nowrap ${data.linkColor ? data.linkColor : ""} ${
                  options.fontSize
                }`}
                {...(data.linkAnalyticsData
                  ? { additionalAnalyticsData: data.linkAnalyticsData }
                  : "")}
                {...((data.legalLinkAccessibilityText && data.legalLinkAccessibilityText.length > 0) ? { 'aria-label': data.legalLinkAccessibilityText} : { 'aria-label': data.legalLinkText})}
                variant= {LinksVariant.none} 
              >
                {data.legalLinkText}
              </Links>
            </>
          ) : (
            ""
          )}
          {data.legalLinkText &&
          data.legalLinkText.length > 0 &&
          options.longLegalBtn ? (
            <span className="block">
              <Links
                {...(data.legalLink ? { href: data.legalLink } : "")}
                onClick={getModalData}
                variant={LinksVariant.none}
                className={`btn-primary mar-t-xs mar-b-xs ${
                  options.btnSize ? options.btnSize : "btn-large"
                }`}
                {...(data.linkAnalyticsData
                  ? { additionalAnalyticsData: data.linkAnalyticsData }
                  : "")}
                {...((data.legalLinkAccessibilityText && data.legalLinkAccessibilityText.length > 0) ? { 'aria-label': data.legalLinkAccessibilityText} : { 'aria-label': data.legalLinkText})}
              >
                {data.legalLinkText}
              </Links>
            </span>
          ) : (
            ""
          )}
        </Legal>
        {showModal && modalBody && !options.dockedModalHeader && (
          <Modal
            size={
              data.legalModalSize
                ? ModalSize[data.legalModalSize]
                : ModalSize.MEDIUM
            }
            responsiveFullScreen={false}
            onClose={() => {
              closeModal();
            }}
            inScrim={false}
            additionalAnalyticsData= {legalModalPageLoad}
          >
            <Modal.Wrapper>
              {modalHeading !== null && (
                <Modal.Header>{modalHeading}</Modal.Header>
              )}
              <Modal.Body legalModal={true}>{modalBody}</Modal.Body>
            </Modal.Wrapper>
          </Modal>
        )}
        {showModal && modalBody && options.dockedModalHeader && (
          <Modal
            size={
              data.legalModalSize
                ? ModalSize[data.legalModalSize]
                : ModalSize.MEDIUM
            }
            responsiveFullScreen={false}
            onClose={() => {
              closeModal();
            }}
            inScrim={false}
            variant={
              options.dockedModalHeader && modalHeading !== null
                ? ModalVariant.SCROLL
                : ModalVariant.DEFAULT
            }
            {...(data.modalLoadAnalyticsData
              ? { additionalAnalyticsData: data.modalLoadAnalyticsData }
              : "")}
          >
            {modalHeading !== null && (
              <Modal.Header>{modalHeading}</Modal.Header>
            )}
            <Modal.Body legalModal={true}>{modalBody}</Modal.Body>
          </Modal>
        )}
      </div>
    );
  };
  return (
    <>
      {options.asStandalone ? (
        <div className={"max-width-background"}>
          <div className="container">
            <div className="row">
              <div className="grid-col-12">{renderLegal()}</div>
            </div>
          </div>
        </div>
      ) : (
        renderLegal()
      )}
      <style global jsx>{`
            .collapsibleSection summary {
                cursor: pointer;
            }
            .collapsibleSection details > summary:first-of-type , h5{
                padding: 2px 0px;
            }
            .collapsibleSection details[open] > details > summary:first-of-type {
                padding: 1px 30px 1px 15px;
            }
            .collapsibleSection details[open] > .list {
              margin-left: 15px;
            }
      `}</style>
    </>
  );
};

export default LegalWrapper;
